<template>
  <b-card
    v-if="materials.length && suppliers.length"
    class="overflow-hidden mb-2"
    no-body
  >
    <div class="comparative-toolbar">
      <div class="comparative-toolbar__title">
        <h4 class="mb-0">
          Comparativa de precios
        </h4>
        <span class="comparative-toolbar__count">
          <template v-if="hiddenSuppliersCount">
            {{ suppliers.length }} de {{ totalSuppliersCount }} proveedores
          </template>
          <template v-else>
            {{ suppliers.length }} {{ suppliers.length === 1 ? 'proveedor' : 'proveedores' }}
          </template>
          ·
          {{ materials.length }} {{ materials.length === 1 ? 'material' : 'materiales' }}
        </span>
      </div>
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="expanded = !expanded"
      >
        <feather-icon
          :icon="expanded ? 'EyeOffIcon' : 'EyeIcon'"
          size="14"
          class="mr-50"
        />
        {{ expanded ? 'Ocultar' : 'Mostrar' }}
      </b-button>
    </div>

    <div
      v-if="expanded"
      class="comparative-scroller"
    >
      <table class="comparative-table">
        <thead>
          <tr>
            <th class="comparative-table__supplier">
              Proveedor
            </th>
            <th
              v-for="material in materials"
              :key="material.key"
              class="comparative-table__material"
            >
              {{ material.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="supplier in suppliers"
            :key="supplier.id"
          >
            <th class="comparative-table__supplier">
              <div class="supplier-name">
                {{ supplier.name }}
                <membership-lock-chip
                  v-if="supplierNamesHidden"
                  :plan-name="paidPlanName"
                />
              </div>
            </th>
            <td
              v-for="material in materials"
              :key="material.key"
              class="comparative-table__cell"
            >
              <div
                v-if="cell(supplier, material)"
                :class="['price', {
                  'price--winner': cell(supplier, material).isWinner,
                  'price--lowest': cell(supplier, material).isLowest && !cell(supplier, material).isWinner,
                }]"
              >
                <div class="price__value">
                  $ {{ cell(supplier, material).unitPrice | money }} MXN
                </div>
                <div
                  v-if="cell(supplier, material).isWinner || cell(supplier, material).isLowest"
                  :class="['price__flag', { 'price__flag--lowest': !cell(supplier, material).isWinner }]"
                >
                  {{ cell(supplier, material).isWinner ? 'Ganador' : 'Menor precio' }}
                </div>
                <div
                  v-if="cell(supplier, material).updatedAt"
                  class="price__updated"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ formatUpdate(cell(supplier, material).updatedAt) }}
                </div>
              </div>
              <span
                v-else
                class="price__empty"
              >—</span>
            </td>
          </tr>
          <tr
            v-if="hiddenSuppliersCount"
            class="comparative-table__locked"
          >
            <th class="comparative-table__supplier">
              <div class="supplier-name supplier-name--locked">
                <span class="locked-dots">•••</span>
              </div>
            </th>
            <td
              :colspan="materials.length"
              class="comparative-table__locked-cell"
            >
              <div class="locked-cta">
                <feather-icon
                  icon="LockIcon"
                  size="16"
                  class="locked-cta__icon"
                />
                <div class="locked-cta__text">
                  <strong>
                    {{ hiddenSuppliersCount }}
                    {{ hiddenSuppliersCount === 1 ? 'proveedor más participó' : 'proveedores más participaron' }}
                    en esta cotización
                  </strong>
                  <span>
                    Tu plan actual te muestra {{ suppliers.length }}.
                    Contrata el plan {{ paidPlanName }} para ver la comparativa completa.
                  </span>
                </div>
                <b-button
                  size="sm"
                  variant="primary"
                  class="locked-cta__button"
                  :to="{ name: 'membership', query: { upgrade: 'comparative_suppliers' } }"
                >
                  Desbloquear
                </b-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-card>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import MembershipLockChip from '@/components/invoice/MembershipLockChip.vue'

export default {
  components: {
    BButton,
    MembershipLockChip,
  },

  props: {
    quote: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // Abierta por default; quien la ve decide si la oculta.
      expanded: true,
    }
  },

  computed: {
    // Lo que el plan le esconde al cliente. Al equipo no le llega, asi que a ellos la
    // comparativa se ve igual que siempre.
    hints() {
      return this.quote?.membership_hints || null
    },

    // Solo se ofrece el upgrade a quien esta en el plan gratuito: una cotizacion que el
    // admin cerro a mano a un cliente de paga no se arregla comprando.
    sellable() {
      return this.hints?.plan_key === 'free'
    },

    supplierNamesHidden() {
      return Boolean(this.sellable && this.hints?.supplier_names_hidden)
    },

    hiddenSuppliersCount() {
      if (!this.sellable) return 0

      return Number(this.hints?.comparative_suppliers_hidden || 0)
    },

    totalSuppliersCount() {
      return Number(this.hints?.comparative_suppliers_total || this.suppliers.length)
    },

    paidPlanName() {
      return this.hints?.paid_plan_name || 'de paga'
    },

    // Una columna por partida. Se usa el id de la partida y no el del producto porque una
    // cotizacion puede traer el mismo material dos veces con cantidades distintas.
    materials() {
      // El payload trae tambien las partidas que se quitaron de la cotizacion, marcadas con
      // active_status false, asi que hay que descartarlas o aparecen materiales que el
      // cliente no esta cotizando.
      return this.items
        .filter(item => item?.id && item.active_status !== false && this.offersOf(item).length)
        // Mismo criterio que la tabla de materiales (compareRowsForDisplay): proveedor
        // ganador, luego nombre de producto, luego id. Asi las columnas de aqui salen en el
        // mismo orden que los renglones de la cotizacion.
        .slice()
        .sort((a, b) => this.compareItems(a, b))
        .map(item => ({
          key: item.id,
          name: item.product_catalog?.name || 'Material',
          productCatalogId: item.product_catalog_id || item.product_catalog?.id,
          selectedSupplierId: item.selected_supplier_branch_id
            ? String(item.selected_supplier_branch_id)
            : '',
          offers: this.offersOf(item),
        }))
    },

    // Filas de proveedor, del mas competitivo al menos. El indice mide cada precio contra
    // el mas bajo de esa misma partida y promedia, asi que es comparable aunque cada
    // proveedor haya cotizado partidas distintas.
    suppliers() {
      const lowestByMaterial = {}
      this.materials.forEach(material => {
        const prices = material.offers.map(o => o.unitPrice)
        if (prices.length) lowestByMaterial[material.key] = Math.min(...prices)
      })

      const bySupplier = {}
      this.materials.forEach(material => {
        material.offers.forEach(offer => {
          if (!bySupplier[offer.supplierId]) {
            bySupplier[offer.supplierId] = {
              id: offer.supplierId,
              name: offer.supplierName,
              cells: {},
              ratios: [],
            }
          }

          const lowest = lowestByMaterial[material.key]
          bySupplier[offer.supplierId].cells[material.key] = {
            unitPrice: offer.unitPrice,
            isWinner: material.selectedSupplierId === offer.supplierId,
            isLowest: lowest > 0 && offer.unitPrice === lowest,
            updatedAt: this.updatedAtFor(offer.supplierId, material.productCatalogId),
          }
          if (lowest > 0) bySupplier[offer.supplierId].ratios.push(offer.unitPrice / lowest)
        })
      })

      return Object.values(bySupplier)
        .map(supplier => ({
          ...supplier,
          quotedCount: supplier.ratios.length,
          priceIndex: supplier.ratios.length
            ? supplier.ratios.reduce((sum, r) => sum + r, 0) / supplier.ratios.length
            : 0,
        }))
        .sort((a, b) => {
          if (a.priceIndex !== b.priceIndex) return a.priceIndex - b.priceIndex
          return b.quotedCount - a.quotedCount
        })
    },
  },

  methods: {
    compareItems(a, b) {
      const keyA = a?.selected_supplier_branch_id ? `supplier_${a.selected_supplier_branch_id}` : ''
      const keyB = b?.selected_supplier_branch_id ? `supplier_${b.selected_supplier_branch_id}` : ''
      if (keyA !== keyB) return keyA.localeCompare(keyB)

      const nameA = (a?.product_catalog?.name || '').trim()
      const nameB = (b?.product_catalog?.name || '').trim()
      if (nameA !== nameB) return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' })

      return String(a?.id || '').localeCompare(String(b?.id || ''))
    },

    offersOf(item) {
      const offers = Array.isArray(item?.unit_prices) ? item.unit_prices : []

      return offers
        .map(offer => ({
          supplierId: offer?.supplier_branch_id ? String(offer.supplier_branch_id) : '',
          supplierName: offer?.supplier_branch_name || 'Proveedor',
          unitPrice: Number(offer?.unit_price),
        }))
        .filter(offer => offer.supplierId && Number.isFinite(offer.unitPrice) && offer.unitPrice > 0)
    },

    updatedAtFor(supplierId, productCatalogId) {
      const map = this.quote?.supplier_price_updates || {}

      return map[supplierId]?.[productCatalogId] || null
    },

    cell(supplier, material) {
      return supplier.cells[material.key] || null
    },

    formatUpdate(isoDate) {
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return ''

      const locale = this.$i18n?.locale || 'es-MX'
      const formatted = new Intl.DateTimeFormat(locale, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }).format(date)

      return `Actualizado: ${formatted}`
    },
  },
}
</script>

<style lang="scss" scoped>
$line: #ebe9f1;
$ink: #25233a;
$muted: #6e6b7b;
$faint: #b2aebf;
$win: #1f9d63;

.comparative-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.5rem;
  border-bottom: 1px solid $line;
}

.comparative-toolbar__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.comparative-toolbar__count {
  font-size: 0.75rem;
  color: $muted;
}

.comparative-scroller {
  overflow: auto;
  max-height: 32rem;
}

.comparative-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.88rem;
}

.comparative-table th,
.comparative-table td {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid $line;
  vertical-align: middle;
}

/* Encabezado fijo al desplazar en vertical, ademas de la columna en horizontal. */
.comparative-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f8f8fa;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $muted;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid #ddd9e6;
}

.comparative-table thead th.comparative-table__supplier {
  text-align: left;
}

/* La columna de proveedor se queda fija al desplazar en horizontal; si no, con muchos
   materiales se pierde de vista de quien es cada renglon. */
.comparative-table__supplier {
  text-align: left;
  min-width: 12.5rem;
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #fff;
  border-right: 1px solid $line;
  font-weight: 400;
}

.comparative-table thead th.comparative-table__supplier {
  z-index: 3;
  background-color: #f8f8fa;
}

.comparative-table tbody tr:hover .comparative-table__supplier {
  background-color: #fafafc;
}

.comparative-table tbody tr:hover td {
  background-color: #fafafc;
}

.comparative-table tbody tr:last-child th,
.comparative-table tbody tr:last-child td {
  border-bottom: none;
}

.supplier-name {
  font-weight: 600;
  color: $ink;
  line-height: 1.3;
}

.comparative-table__locked {
  background: #fbfaff;
}

.comparative-table__locked .comparative-table__supplier {
  vertical-align: middle;
}

.supplier-name--locked {
  color: $faint;
}

.locked-dots {
  display: inline-block;
  font-size: 1.35rem;
  letter-spacing: 0.12em;
  line-height: 1;
}

.comparative-table__locked-cell {
  padding: 0.85rem 1rem;
}

.locked-cta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  text-align: left;
}

.locked-cta__icon {
  flex-shrink: 0;
  color: $muted;
}

.locked-cta__text {
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: $ink;
    font-size: 0.9rem;
  }

  span {
    color: $muted;
    font-size: 0.78rem;
  }
}

.locked-cta__button {
  flex-shrink: 0;
  margin-left: auto;
}

.comparative-table__cell {
  text-align: center;
}

.price {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  line-height: 1.25;
  border-radius: 0.5rem;
  padding: 0.4rem 0.8rem;
  min-width: 8.5rem;
}

.price--winner {
  background-color: rgba(40, 199, 111, 0.12);
  box-shadow: inset 0 0 0 1px rgba(40, 199, 111, 0.35);
}

/* Gris, no un verde tenue: el verde es del ganador y dos verdes se confunden. */
.price--lowest {
  background-color: rgba(110, 107, 123, 0.09);
  box-shadow: inset 0 0 0 1px rgba(110, 107, 123, 0.2);
}

.price__value {
  font-weight: 700;
  color: $ink;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
}

.price--winner .price__value {
  color: $win;
}

.price__flag {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $win;
}

.price__flag--lowest {
  color: #5e5a6e;
}

.price__updated {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  color: $muted;
  white-space: nowrap;
}

.price__empty {
  color: $faint;
  font-weight: 600;
}

@media (max-width: 767px) {
  .comparative-table th,
  .comparative-table td {
    padding: 0.55rem 0.7rem;
  }

  .price {
    min-width: 0;
    padding: 0.3rem 0.5rem;
  }

  .price__updated {
    display: none;
  }
}
</style>
